<template>
  <div class="kpi" :class="tone">
    <span class="kpi-label">{{ label }}</span>
    <strong class="kpi-value">{{ value }}</strong>
    <span v-if="hasGrowth" class="kpi-growth-row">
      <span class="kpi-growth" :class="growthClass">
        {{ growth > 0 ? '▲' : growth < 0 ? '▼' : '=' }} {{ growthText }}
        <span v-if="previousTitle" class="kpi-tip" role="tooltip">{{ previousTitle }}</span>
      </span>
    </span>
    <span v-else-if="hint" class="kpi-hint">{{ hint }}</span>
  </div>
</template>

<script>
import {percent} from "@/statistics/utils/format";

/**
 * Indicateur clé : libellé, valeur et, si elle est fournie, l'évolution par rapport à la période précédente.
 * inverse : une baisse est une bonne nouvelle (ex : retards de paiement).
 */
export default {
  name: 'StatisticsKpi',
  props: {
    label: {type: String, required: true},
    value: {type: String, required: true},
    growth: {type: Number, default: undefined},
    previous: {type: String, default: ''},
    hint: {type: String, default: ''},
    inverse: {type: Boolean, default: false},
    tone: {type: String, default: ''},
  },
  computed: {
    hasGrowth() {
      return this.growth !== undefined && this.growth !== null;
    },
    growthText() {
      return percent(Math.abs(this.growth));
    },
    growthClass() {
      if (!this.growth) return 'flat';
      return (this.growth > 0) !== this.inverse ? 'good' : 'bad';
    },
    previousTitle() {
      return this.previous ? `Période précédente : ${this.previous}` : '';
    },
  },
};
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.kpi
  display flex
  flex-direction column
  gap 8px
  padding 20px 22px
  background white
  border 1px solid PAGE_RULE
  border-radius 16px
  box-shadow 0 1px 0 rgba(23, 35, 63, .04), 0 12px 28px -20px rgba(23, 35, 63, .4)

  &.warn
    border-color WARNING

  // Valeur à surveiller (ex : factures en retard)
  &.bad
    border-color ERROR

    .kpi-value
      color CTRL_ERROR

.kpi-label
  font-size 12.5px
  font-weight 600
  color CTRL_TEXT_MUTED

.kpi-value
  font-size 28px
  line-height 1.1
  color THEME_COLOR_PRIMARY
  font-variant-numeric tabular-nums

.kpi-hint
  font-size 12px
  color CTRL_TEXT_MUTED

// Évolution par rapport à la période précédente : pastille, avec la valeur précédente en infobulle
.kpi-growth-row
  display flex
  align-items center
  flex-wrap wrap
  gap 4px 8px

.kpi-vs
  font-size 11px
  font-weight 400
  color CTRL_TEXT_MUTED

.kpi-growth
  position relative
  display inline-flex
  align-items center
  gap 6px
  white-space nowrap
  height 24px
  padding 0 10px
  border-radius 12px
  background #f0f1f3
  font-size 12px
  font-weight 600
  color CTRL_TEXT_MUTED
  cursor help

  small
    font-size 11px
    font-weight 400

  &.good
    background #E1F3E6
    color #1f6a2c

  &.bad
    background #FBE4DF
    color #a3283d

  .kpi-tip
    position absolute
    left 0
    bottom unquote("calc(100% + 8px)")
    z-index 5
    display none
    padding 7px 11px
    border-radius 8px
    background-color #17233f
    color white
    font-size 12px
    font-weight 600
    white-space nowrap

    &::after
      content ""
      position absolute
      left 16px
      top 100%
      border 6px solid transparent
      border-top-color #17233f

  &:hover .kpi-tip
    display block
</style>
